import React, { useState, useEffect } from 'react';
import { Trophy, Medal, Award, Crown, TrendingUp, Flame, User } from 'lucide-react';
import { useLeaderboard, useUserRank } from '../../hooks/useGamification';
import { LeaderboardEntry } from '../../constants/gamification';

interface LeaderboardProps {
  userId?: string;
  limitCount?: number;
  showCurrentUser?: boolean;
  compact?: boolean;
}

const Leaderboard: React.FC<LeaderboardProps> = ({
  userId,
  limitCount = 50,
  showCurrentUser = true,
  compact = false
}) => {
  const { leaderboard, loading: leaderboardLoading } = useLeaderboard('alltime', limitCount);
  const { rank: userRank, loading: rankLoading } = useUserRank(userId || '');

  // Get rank medal/icon
  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Crown className="w-6 h-6 text-yellow-500" />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-400" />;
      case 3:
        return <Award className="w-6 h-6 text-amber-600" />;
      default:
        return <span className="text-gray-600 font-bold">{rank}</span>;
    }
  };

  // Get rank background color
  const getRankBgColor = (rank: number, isCurrentUser: boolean) => {
    if (isCurrentUser) return 'bg-red-50 border-2 border-red-500';
    if (rank === 1) return 'bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-300';
    if (rank === 2) return 'bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-300';
    if (rank === 3) return 'bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-300';
    return 'bg-white hover:bg-gray-50 border border-gray-200';
  };

  // Format points with commas
  const formatPoints = (points: number | undefined) => {
    return (points || 0).toLocaleString();
  };

  // Loading state
  if (leaderboardLoading && !leaderboard.length) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <span className="ml-3 text-gray-600">Loading leaderboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${compact ? 'p-4' : 'p-6'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Trophy className="w-6 h-6 text-red-600" />
          <h2 className={`${compact ? 'text-lg' : 'text-xl'} font-bold text-gray-900`}>
            Leaderboard - All Time
          </h2>
        </div>
      </div>

      {/* Current User Rank (if not in top list) */}
      {showCurrentUser && userId && userRank && userRank.rank > limitCount && (
        <div className="mb-4 p-4 bg-red-50 border-2 border-red-500 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center text-white font-bold">
                {userRank.rank}
              </div>
              <div>
                <p className="font-semibold text-gray-900">Your Rank</p>
                <p className="text-sm text-gray-600">Keep climbing! 🚀</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-red-600">{formatPoints(userRank.points)}</p>
              <p className="text-xs text-gray-500">points</p>
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard List */}
      {leaderboard.length === 0 ? (
        <div className="text-center py-12">
          <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No rankings yet for this period</p>
          <p className="text-sm text-gray-400 mt-2">Be the first to climb the leaderboard!</p>
        </div>
      ) : (
        <div className="space-y-2">
          {leaderboard.map((entry, index) => {
            const isCurrentUser = userId && entry.userId === userId;
            const rank = index + 1;

            return (
              <div
                key={entry.userId}
                className={`
                  flex items-center justify-between p-4 rounded-lg transition-all
                  ${getRankBgColor(rank, isCurrentUser)}
                `}
              >
                {/* Rank & User Info */}
                <div className="flex items-center space-x-4 flex-1">
                  {/* Rank Icon */}
                  <div className="w-10 h-10 flex items-center justify-center">
                    {getRankIcon(rank)}
                  </div>

                  {/* User Avatar & Name */}
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center text-white font-bold
                      ${rank === 1 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' :
                        rank === 2 ? 'bg-gradient-to-br from-gray-400 to-gray-600' :
                        rank === 3 ? 'bg-gradient-to-br from-amber-400 to-amber-600' :
                        'bg-gradient-to-br from-red-400 to-red-600'
                      }
                    `}>
                      {entry.userName ? entry.userName.charAt(0).toUpperCase() : <User className="w-5 h-5" />}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">
                        {entry.userName || 'Anonymous User'}
                        {isCurrentUser && (
                          <span className="ml-2 text-xs bg-red-600 text-white px-2 py-1 rounded-full">
                            You
                          </span>
                        )}
                      </p>
                      <div className="flex flex-col space-y-1">
                        {entry.userEmail && (
                          <p className="text-xs text-gray-500 truncate">
                            {entry.userEmail}
                          </p>
                        )}
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <span className="capitalize">{entry.levelName || 'Explorer'}</span>
                          <span>•</span>
                          <span className="flex items-center space-x-1">
                            <Flame className="w-3 h-3 text-orange-500" />
                            <span>{entry.currentStreak || 0} day streak</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Points Display */}
                <div className="text-right ml-4">
                  <p className={`text-xl font-bold ${
                    rank === 1 ? 'text-yellow-600' :
                    rank === 2 ? 'text-gray-600' :
                    rank === 3 ? 'text-amber-600' :
                    'text-gray-900'
                  }`}>
                    {formatPoints(entry.points)}
                  </p>
                  <p className="text-xs text-gray-500">points</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer Info */}
      {!compact && leaderboard.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-500 text-center">
            Rankings based on streak points • Updates in real-time
          </p>
        </div>
      )}
    </div>
  );
};

export default Leaderboard;
